import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Paper,
  Chip,
  Alert,
  Divider,
  Select,
  MenuItem,
  Card,
  CardContent,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { ClinicDialog } from '../components/dialogs/ClinicDialog';
import { CLINIC_DIALOG_SX } from '../constants/popupStyles';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as ContentCopyIcon,
  Sms as SmsIcon,
} from '@mui/icons-material';
import smsTemplateService, {
  SmsTemplate,
  ContextOption,
  SmsTemplateCreate,
  AvailableVariable,
} from '../services/smsTemplateService';
import { useAuth } from '../context/AuthContext';
import ContextSelect from '../components/common/ContextSelect';
import { formatDate } from '../utils/dateUtils';
import { useUserTimezone } from '../hooks/useUserTimezone';
import ManageColumnsModal from '../components/modals/ManageColumnsModal';
import SaveViewModal from '../components/modals/SaveViewModal';
import DataTableToolbar from '../components/table/DataTableToolbar';
import DataTableContainer from '../components/table/DataTableContainer';
import DataTableBody from '../components/table/DataTableBody';
import MobileCardGrid from '../components/table/MobileCardGrid';
import TablePageLayout from '../components/table/TablePageLayout';
import useTablePage from '../components/table/hooks/useTablePage';
import { RowContextMenu, ContextMenuOption } from '../components/ui/RowContextMenu';
import { smsTemplateColumns } from '../config/smsTemplateColumns';
import { getApiError } from '../utils/apiError';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const SmsTemplatesPage: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [success, setSuccess] = useState<string | null>(null);
  useUserTimezone();

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<SmsTemplate | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Available variables
  const [availableVariables, setAvailableVariables] = useState<AvailableVariable[]>([]);

  // Contexts from API
  const [contexts, setContexts] = useState<ContextOption[]>([]);
  const contextLabelMap = useMemo(() => new Map(contexts.map(c => [c.value, c.label])), [contexts]);

  // Preview data
  const [previewResult] = useState<string>('');

  // Form data
  const [formData, setFormData] = useState<{
    context: string;
    name: string;
    internal_id: string;
    content: string;
    language: string;
    is_active: boolean;
  }>({
    context: 'PATIENT',
    name: '',
    internal_id: '',
    content: '',
    language: 'en',
    is_active: true,
  });

  const getSortValue = useCallback((template: SmsTemplate, key: string): any => {
    switch (key) {
      case 'name': return template.name.toLowerCase();
      case 'internal_id': return (template.internal_id || '').toLowerCase();
      case 'context': return template.context.toLowerCase();
      case 'language': return template.language;
      case 'status': return template.is_active ? 'active' : 'inactive';
      case 'createdAt': return template.created_at;
      default: return '';
    }
  }, []);

  const getSearchText = useCallback((template: SmsTemplate): string[] => {
    return [template.name, template.internal_id || '', template.context];
  }, []);

  const table = useTablePage<SmsTemplate>({
    entityType: 'smsTemplates',
    columns: smsTemplateColumns,
    getSortValue,
    getSearchText,
  });

  useEffect(() => {
    fetchContexts();
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchContexts = async () => {
    try {
      const fetchedContexts = await smsTemplateService.getContexts();
      setContexts(fetchedContexts);
    } catch (err: any) {
      console.error('Failed to load contexts:', err);
    }
  };

  const fetchTemplates = () => {
    table.loadData(() => smsTemplateService.getTemplates(undefined, undefined), 'Failed to load SMS templates');
  };

  const handleEdit = async () => {
    if (!table.selectedItem) return;
    setSaveError(null);
    setEditingTemplate(table.selectedItem);
    setFormData({
      context: table.selectedItem.context,
      name: table.selectedItem.name,
      internal_id: table.selectedItem.internal_id || '',
      content: table.selectedItem.content,
      language: table.selectedItem.language || 'en',
      is_active: table.selectedItem.is_active,
    });
    setTabValue(0);
    await loadAvailableVariables(table.selectedItem.context);
    setEditDialogOpen(true);
    table.handleMenuClose();
  };

  const handleAddNew = async () => {
    setSaveError(null);
    setEditingTemplate(null);
    setFormData({
      context: 'PATIENT',
      name: '',
      internal_id: '',
      content: '',
      language: 'en',
      is_active: true,
    });
    setTabValue(0);
    await loadAvailableVariables('PATIENT');
    setEditDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!table.selectedItem || !window.confirm(`Are you sure you want to delete "${table.selectedItem.name}"?`)) {
      return;
    }
    try {
      await smsTemplateService.deleteTemplate(table.selectedItem.id);
      setSuccess('Template deleted successfully');
      fetchTemplates();
    } catch (err: any) {
      table.setError(getApiError(err, 'Failed to delete template'));
    }
    table.handleMenuClose();
  };

  const handleDuplicate = async () => {
    if (!table.selectedItem) return;
    setEditingTemplate(null);
    setFormData({
      context: table.selectedItem.context,
      name: `Copy of ${table.selectedItem.name}`,
      internal_id: '',
      content: table.selectedItem.content,
      language: table.selectedItem.language || 'en',
      is_active: table.selectedItem.is_active,
    });
    setTabValue(0);
    await loadAvailableVariables(table.selectedItem.context);
    setEditDialogOpen(true);
    table.handleMenuClose();
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.content.trim()) {
      setSaveError('Name and content are required');
      return;
    }

    setSaveError(null);
    try {
      if (editingTemplate) {
        await smsTemplateService.updateTemplate(editingTemplate.id, formData);
        setSuccess('Template updated successfully');
      } else {
        const createPayload: SmsTemplateCreate = formData;
        await smsTemplateService.createTemplate(createPayload);
        setSuccess('Template created successfully');
      }
      setEditDialogOpen(false);
      setFormData({
        context: 'PATIENT',
        name: '',
        internal_id: '',
        content: '',
        language: 'en',
        is_active: true,
      });
      setEditingTemplate(null);
      fetchTemplates();
    } catch (err: any) {
      setSaveError(getApiError(err, 'Failed to save template'));
    }
  };

  const loadAvailableVariables = async (context: string) => {
    try {
      const vars = await smsTemplateService.getAvailableVariables(context);
      setAvailableVariables(vars);
    } catch (err: any) {
      console.error('Failed to load available variables:', err);
    }
  };

  const handleContextChange = async (newContext: string) => {
    setFormData({ ...formData, context: newContext });
    await loadAvailableVariables(newContext);
  };

  const insertVariable = (variableName: string) => {
    const textarea = document.getElementById('template-content') as HTMLTextAreaElement;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const before = formData.content.substring(0, start);
      const after = formData.content.substring(end);
      setFormData({
        ...formData,
        content: `${before}{${variableName}}${after}`,
      });
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + variableName.length + 2, start + variableName.length + 2);
      }, 0);
    }
  };

  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSuccess('Copied to clipboard');
  };

  const languageLabel = (lang: string) => lang === 'he' ? 'Hebrew' : 'English';

  // Column value renderer
  const getColumnValue = (template: SmsTemplate, columnKey: string): React.ReactNode => {
    switch (columnKey) {
      case 'name': return template.name;
      case 'internal_id': return template.internal_id || '';
      case 'context': return <Chip label={contextLabelMap.get(template.context) || template.context} size="small" />;
      case 'language': return <Chip label={languageLabel(template.language)} size="small" />;
      case 'status':
        return (
          <Chip
            label={template.is_active ? 'Active' : 'Inactive'}
            color={template.is_active ? 'success' : 'default'}
            size="small"
          />
        );
      case 'createdAt': return formatDate(template.created_at);
      default: return '';
    }
  };

  // Mobile card renderer
  const renderMobileCard = (template: SmsTemplate) => (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1} sx={{ pr: 5 }}>
        <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
          {template.name}
        </Typography>
        <Chip
          label={template.is_active ? 'Active' : 'Inactive'}
          color={template.is_active ? 'success' : 'default'}
          size="small"
        />
      </Box>
      <Box>
        <Chip label={contextLabelMap.get(template.context) || template.context} size="small" sx={{ mr: 1 }} />
        <Typography variant="caption" color="text.secondary">
          {formatDate(template.created_at)}
        </Typography>
      </Box>
    </>
  );

  const getContextMenuOptions = (): ContextMenuOption[] => {
    if (!table.selectedItem) return [];
    return [
      { label: 'Edit', icon: <EditIcon sx={{ mr: 2 }} />, onClick: handleEdit },
      { label: 'Duplicate', icon: <ContentCopyIcon sx={{ mr: 2 }} />, onClick: handleDuplicate },
      { label: 'Delete', icon: <DeleteIcon sx={{ mr: 2 }} />, onClick: handleDelete, color: 'error' as const },
    ];
  };

  // Check if user has email_and_sms_templates permission
  if (!user?.user_privilege?.email_and_sms_templates) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          You don't have permission to access SMS templates. Please contact your administrator.
        </Alert>
      </Box>
    );
  }

  return (
    <TablePageLayout loading={table.loading} error={table.error} errorPrefix="Error loading SMS templates:">
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <DataTableToolbar
        {...table.toolbarProps}
        title="SMS Templates"
        searchPlaceholder="Search templates by name or context..."
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddNew}
            sx={{ whiteSpace: 'nowrap' }}
          >
            New Template
          </Button>
        }
      />

      {isMobile ? (
        <MobileCardGrid
          data={table.sortedData}
          renderCard={renderMobileCard}
          onMenuClick={table.handleRowClick}
          getItemKey={(t) => t.id}
          emptyIcon={<SmsIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />}
          emptyMessage="No SMS templates found"
        />
      ) : (
        <DataTableContainer {...table.containerProps}>
          <DataTableBody
            data={table.sortedData}
            {...table.tableBodyProps}
            getColumnValue={getColumnValue}
            getRowKey={(t) => t.id}
            onRowClick={table.handleRowClick}
          />
        </DataTableContainer>
      )}

      <RowContextMenu {...table.menuProps} options={getContextMenuOptions()} />

      {/* Edit Dialog */}
      <ClinicDialog
        open={editDialogOpen}
        onClose={() => { setEditDialogOpen(false); setSaveError(null); }}
        title={editingTemplate ? 'Edit SMS Template' : 'Create SMS Template'}
        maxWidth="md"
        actions={
          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
            {saveError && (
              <Alert severity="error" onClose={() => setSaveError(null)}>
                {saveError}
              </Alert>
            )}
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
              <Button onClick={handleSave} variant="contained" sx={CLINIC_DIALOG_SX.primaryButton}>
                {editingTemplate ? 'Update' : 'Create'}
              </Button>
              <Button onClick={() => { setEditDialogOpen(false); setSaveError(null); }} sx={CLINIC_DIALOG_SX.cancelButton}>
                Cancel
              </Button>
            </Box>
          </Box>
        }
      >
        <Box sx={{ pt: 1 }}>
          <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
            <Tab label="Template Details" />
            <Tab label="Variables Reference" />
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={CLINIC_DIALOG_SX.fieldRow}>
                <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>
                  Name
                </Typography>
                <TextField
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  fullWidth
                  size="small"
                  placeholder="e.g., Patient Appointment Reminder"
                  InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
                />
              </Box>

              <Box sx={CLINIC_DIALOG_SX.fieldRow}>
                <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>
                  Internal ID
                </Typography>
                <TextField
                  value={formData.internal_id}
                  onChange={(e) => setFormData({ ...formData, internal_id: e.target.value })}
                  fullWidth
                  size="small"
                  placeholder="Optional internal identifier"
                  InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
                />
              </Box>

              <ContextSelect
                contexts={contexts}
                value={formData.context}
                onChange={handleContextChange}
              />

              <Box sx={CLINIC_DIALOG_SX.fieldRow}>
                <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>
                  Language
                </Typography>
                <Select
                  value={formData.language}
                  onChange={(e) => setFormData({ ...formData, language: e.target.value as string })}
                  fullWidth
                  sx={CLINIC_DIALOG_SX.select}
                >
                  <MenuItem value="en">English</MenuItem>
                  <MenuItem value="he">Hebrew</MenuItem>
                </Select>
              </Box>

              <Box sx={CLINIC_DIALOG_SX.fieldRow}>
                <Typography variant="body2" sx={{ ...CLINIC_DIALOG_SX.fieldLabel, alignSelf: 'flex-start', mt: 1 }}>
                  Content
                </Typography>
                <TextField
                  id="template-content"
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  fullWidth
                  size="small"
                  multiline
                  rows={6}
                  placeholder="Use {variable_name} for placeholders"
                  helperText="Use {variable_name} to insert variables. Check the Variables Reference tab for available variables."
                  InputProps={{
                    style: { ...CLINIC_DIALOG_SX.textField as React.CSSProperties, direction: formData.language === 'he' ? 'rtl' : 'ltr' },
                  }}
                />
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Quick Insert Variables:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {availableVariables.map((variable) => (
                    <Chip
                      key={variable.name}
                      label={variable.name}
                      onClick={() => insertVariable(variable.name)}
                      size="small"
                      icon={<ContentCopyIcon />}
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="subtitle1">Available Variables for {contextLabelMap.get(formData.context) || formData.context} Context:</Typography>
              <Divider />
              {availableVariables.map((variable) => (
                <Card key={variable.name} variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                      {variable.name}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ my: 1 }}>
                      {variable.description}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace', backgroundColor: 'grey.100', p: 1, borderRadius: 1 }}>
                        Example: {variable.example}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleCopyToClipboard(`{${variable.name}}`)}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </TabPanel>
        </Box>
      </ClinicDialog>

      {/* Preview Dialog */}
      <Dialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Template Preview</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Formatted SMS:
            </Typography>
            <Paper
              sx={{
                p: 2,
                backgroundColor: 'grey.50',
                minHeight: 100,
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap',
              }}
            >
              {previewResult}
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <ManageColumnsModal {...table.manageColumnsProps} />
      <SaveViewModal {...table.saveViewModalProps} />
    </TablePageLayout>
  );
};

export default SmsTemplatesPage;
